import type { Funnel, Lead, WhatsappChat, WhatsappSession } from "../types.ts";
import { sortChatsForList } from "./Chat.helpers.ts";
import { getLeadCentralCampaign } from "./kanban/leadCampaign.ts";

export function getActiveFunnel(funnels: Funnel[], activeFunnelId: string): Funnel | null {
  return funnels.find((funnel) => funnel.id === activeFunnelId) || funnels[0] || null;
}

export function filterLeadsForFunnel(leads: Lead[], funnelId: string, query: string): Lead[] {
  const leadsInFunnel = collapseLegacyLeadDuplicates(leads.filter((lead) => lead.funnelId === funnelId));
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return leadsInFunnel;

  return leadsInFunnel.filter((lead) => {
    const searchable = [
      lead.name,
      lead.phone,
      lead.email,
      lead.course,
      getLeadCentralCampaign(lead)?.label,
    ].map((value) => String(value || "").toLowerCase()).join(" ");
    return searchable.includes(normalizedQuery);
  });
}

export function collapseLegacyLeadDuplicates(leads: Lead[]): Lead[] {
  const phoneLeads = leads.filter((lead) => !!normalizeLeadPhone(lead.phone));
  const processed = new Set<string>();
  const out: Lead[] = [];

  for (const lead of leads) {
    const id = String(lead.id);
    if (processed.has(id)) continue;

    const phone = normalizeLeadPhone(lead.phone);
    if (!phone) {
      const candidates = matchingPhoneCandidates(lead, phoneLeads);
      if (candidates.length === 1) continue;
      out.push(lead);
      processed.add(id);
      continue;
    }

    const samePhone = leads.filter((item) => normalizeLeadPhone(item.phone) === phone);
    const legacyMatches = leads.filter((item) => {
      if (normalizeLeadPhone(item.phone)) return false;
      const candidates = matchingPhoneCandidates(item, phoneLeads);
      return candidates.length === 1 && String(candidates[0].id) === id;
    });
    const members = uniqueLeads([lead, ...samePhone, ...legacyMatches]);
    for (const member of members) processed.add(String(member.id));
    out.push(mergeLeadCluster(lead, members));
  }

  return out;
}

export function leadsForStage(leads: Lead[], stageId: string): Lead[] {
  return leads.filter((lead) => lead.stage === stageId);
}

export function replaceLead(leads: Lead[], nextLead: Lead): Lead[] {
  const id = String(nextLead.id);
  const found = leads.some((lead) => String(lead.id) === id);
  if (!found) return [...leads, nextLead];
  return leads.map((lead) => (String(lead.id) === id ? nextLead : lead));
}

export function normalizePhone(phone: unknown): string {
  return String(phone || "").replace(/\D+/g, "");
}

function normalizeLeadPhone(phone: unknown): string {
  const digits = normalizePhone(phone);
  if ((digits.length === 10 || digits.length === 11) && !digits.startsWith("55")) {
    return `55${digits}`;
  }
  return digits;
}

function matchingPhoneCandidates(legacyLead: Lead, phoneLeads: Lead[]): Lead[] {
  const name = comparableLeadText(legacyLead.name);
  const course = comparableLeadText(legacyLead.course);
  const funnelId = String(legacyLead.funnelId || "");
  if (!name || !course || !funnelId) return [];
  return phoneLeads.filter((candidate) => (
    String(candidate.funnelId || "") === funnelId
    && comparableLeadText(candidate.name) === name
    && comparableLeadText(candidate.course) === course
  ));
}

function comparableLeadText(value: unknown): string {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function uniqueLeads(leads: Lead[]): Lead[] {
  const seen = new Set<string>();
  return leads.filter((lead) => {
    const id = String(lead.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function mergeLeadCluster(canonical: Lead, members: Lead[]): Lead {
  const merged: Lead = { ...canonical };
  for (const source of members) {
    mergeStringField(merged, source, "email");
    mergeStringField(merged, source, "course");
    mergeStringField(merged, source, "lastMessageText");
    mergeStringField(merged, source, "whatsappSubtitle");
    mergeStringField(merged, source, "notes");
    mergeStringField(merged, source, "lastCapturedAt");
    mergeArrayField(merged, source, "recentMessages");
    mergeArrayField(merged, source, "cadenceItems");
    mergeArrayField(merged, source, "detectedEmails");
    mergeArrayField(merged, source, "detectedLinks");
    const tags = uniqueStrings([...(merged.issueTags || []), ...(source.issueTags || [])]);
    if (tags.length) merged.issueTags = tags;
    const sourceInteractions = Number(source.interactionCount || 0);
    const mergedInteractions = Number(merged.interactionCount || 0);
    if (sourceInteractions > 0 || mergedInteractions > 0) {
      merged.interactionCount = Math.max(mergedInteractions, sourceInteractions);
    }
    if (isAfter(source.updatedAt, merged.updatedAt)) merged.updatedAt = source.updatedAt;
  }
  return merged;
}

function mergeStringField(merged: Lead, source: Lead, field: string): void {
  if (!String(merged[field] || "").trim() && String(source[field] || "").trim()) {
    merged[field] = source[field];
  }
}

function mergeArrayField(merged: Lead, source: Lead, field: string): void {
  const current = Array.isArray(merged[field]) ? merged[field] as unknown[] : [];
  const next = Array.isArray(source[field]) ? source[field] as unknown[] : [];
  if (next.length > current.length) merged[field] = next;
}

function uniqueStrings(values: unknown[]): string[] {
  return Array.from(new Set(values.map((value) => String(value || "").trim()).filter(Boolean)));
}

function isAfter(left: unknown, right: unknown): boolean {
  const leftTime = typeof left === "number" ? left : Date.parse(String(left || ""));
  const rightTime = typeof right === "number" ? right : Date.parse(String(right || ""));
  return Number.isFinite(leftTime) && (!Number.isFinite(rightTime) || leftTime > rightTime);
}

function comparableChatPhone(value: unknown): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (!raw.includes("@")) return normalizeLeadPhone(raw);

  const localPart = raw.split("@")[0].split(":")[0];
  if (!/^\d+$/.test(localPart)) return "";
  return normalizeLeadPhone(localPart);
}

export function chatJidForLeadPhone(phone: unknown): string {
  const digits = normalizeLeadPhone(phone);
  if (digits.length < 8 || digits.length > 15) return "";
  return `${digits}@s.whatsapp.net`;
}

export function cleanLeadPreviewText(value: unknown): string {
  let text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";

  text = text.replace(/tail-(?:in|out)/gi, "").trim();
  text = text.replace(/(?:\s*\d{1,2}:\d{2}:?)+$/g, "").trim();
  text = text.replace(/\s+([,.!?;:])/g, "$1");

  return text;
}

export function findChatForLead(lead: Lead, chats: WhatsappChat[]): WhatsappChat | null {
  const phone = normalizeLeadPhone(lead.phone);
  if (!phone) return null;
  return chats.find((chat) => (
    comparableChatPhone(chat.phone) === phone
    || comparableChatPhone(chat.providerChatId) === phone
    || comparableChatPhone(chat.chatJid) === phone
  )) || null;
}

export function isWhatsappAccount(account: WhatsappSession | null | undefined): boolean {
  return !!account && (!account.accountType || account.accountType === "whatsapp");
}

export function preferredWhatsappAccount(
  accounts: WhatsappSession[],
  accountFilter: string[],
  whatsappSession: WhatsappSession | null,
): WhatsappSession | null {
  const whatsappAccounts = (accounts || []).filter(isWhatsappAccount);
  const unipileAccounts = whatsappAccounts.filter((account) => account.provider === "unipile");
  const selectedAccountId = (accountFilter || [])[0] || "";
  const selectedAccount = selectedAccountId
    ? whatsappAccounts.find((account) => account.sessionId === selectedAccountId)
    : null;
  const selectedUnipileAccount = selectedAccount?.provider === "unipile" ? selectedAccount : null;
  const defaultUnipileAccount = unipileAccounts.find((account) => account.isDefault) || null;
  const sessionIsUnipile = whatsappSession?.provider === "unipile" && isWhatsappAccount(whatsappSession);

  return selectedUnipileAccount
    || defaultUnipileAccount
    || (sessionIsUnipile ? whatsappSession : null)
    || unipileAccounts[0]
    || selectedAccount
    || whatsappAccounts.find((account) => account.isDefault)
    || (isWhatsappAccount(whatsappSession) ? whatsappSession : null)
    || whatsappAccounts[0]
    || null;
}

export function openChatPatchForLead(
  lead: Lead,
  chats: WhatsappChat[],
  account: WhatsappSession | null,
): { patch?: { panel: "chats"; activeChatJid: string; chats?: WhatsappChat[]; composer?: { chatJid: string; text: string } }; error?: string } {
  if (!normalizePhone(lead.phone)) {
    return { error: "Lead sem telefone" };
  }
  const chat = findChatForLead(lead, chats);
  if (chat) {
    return { patch: { panel: "chats", activeChatJid: chat.chatJid } };
  }
  const draftChat = buildDraftChatForLead(lead, account);
  if (!draftChat) {
    return { error: "Nao foi possivel iniciar a conversa desse lead" };
  }
  return {
    patch: {
      panel: "chats",
      activeChatJid: draftChat.chatJid,
      chats: sortChatsForList([
        draftChat,
        ...chats.filter((item) => item.chatJid !== draftChat.chatJid),
      ]),
      composer: { chatJid: draftChat.chatJid, text: "" },
    },
  };
}

export function buildDraftChatForLead(
  lead: Lead,
  account: WhatsappSession | null | undefined,
  now = Date.now(),
): WhatsappChat | null {
  const chatJid = chatJidForLeadPhone(lead.phone);
  if (!chatJid) return null;
  const phone = chatJid.split("@")[0];
  const pushName = String(lead.name || "").trim() || phone;
  return {
    chatJid,
    provider: account?.provider || "unipile",
    accountSessionId: account?.sessionId || "",
    providerChatId: chatJid,
    isGroup: false,
    pushName,
    phone,
    lastMsgId: "",
    lastText: "",
    lastKind: "",
    lastFromMe: false,
    lastStatus: 0,
    lastTsMs: now,
    unreadCount: 0,
    archived: false,
    leadId: lead.id,
    tagIds: [],
  };
}
